import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Wallet, Check } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import LanguageSelector from "@/components/landing/LanguageSelector";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState(null);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const connectWallet = async () => {
    setConnecting(true);
    try {
      if (window.ethereum) {
        const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
        if (accounts?.[0]) setAccount(accounts[0]);
      } else {
        // demo fallback wallet address
        await new Promise((r) => setTimeout(r, 700));
        setAccount("0x7A3f" + Math.random().toString(16).slice(2, 6) + "...9E4c");
      }
    } catch (e) {
      console.error("Wallet connection cancelled", e);
    } finally {
      setConnecting(false);
    }
  };

  const short = (a) => (a.length > 14 ? `${a.slice(0, 6)}...${a.slice(-4)}` : a);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2.5" : "py-4"
      }`}
      data-testid="navbar"
    >
      <div className="container-trt">
        <div className={`flex items-center justify-between rounded-2xl px-4 sm:px-5 py-3 transition-all duration-500 ${
          scrolled ? "glass gold-glow" : "bg-transparent"
        }`}>
          {/* Logo */}
          <a href="#top" className="flex items-center gap-2.5 group" data-testid="logo">
            <img
              src="/trst-logo.png"
              alt="TRT logo"
              className="w-9 h-9 rounded-full object-contain drop-shadow-[0_0_10px_rgba(244,201,93,0.45)] transition-transform duration-500 group-hover:rotate-12"
            />
            <div className="leading-none">
              <div className="font-display font-extrabold text-lg tracking-tight">TRT</div>
              <div className="text-[9px] font-mono tracking-[0.2em] text-[var(--muted)]">THE REAL TOKEN</div>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="px-3 py-2 text-sm text-[var(--muted)] hover:text-[var(--gold-2)] transition-colors relative group"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
                <span className="absolute left-3 right-3 -bottom-0.5 h-px scale-x-0 group-hover:scale-x-100 origin-left transition-transform gold-line" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSelector />
            <a 
            href="/login/"
              className="btn-gold text-sm !px-4 !py-2.5 hidden sm:inline-flex"
              data-testid="connect-wallet-btn"
            >
              Login
            </a>
            
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden mt-2 glass rounded-2xl"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col p-3">
                {NAV_LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="px-3 py-3 text-sm text-[var(--muted)] hover:text-[var(--gold-2)] border-b border-white/5 last:border-0"
                  >
                    {l.label}
                  </a>
                ))}
                <div className="px-3 py-3 border-b border-white/5">
                  <LanguageSelector compact />
                </div>
                <button onClick={connectWallet} className="btn-gold text-sm mt-3 justify-center">
                  <Wallet className="w-4 h-4" /> {account ? short(account) : "Connect Wallet"}
                </button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
